<template>
  <ContentWrap>
    <el-alert type="info" :closable="false" show-icon class="mb-12px" title="公共文档：先建分类再上传。本所 firm_id 隔离（与 Admin 代管同源服务）。" />
    <el-form :inline="true" class="mb-12px">
      <el-form-item><el-button type="primary" @click="getList">刷新</el-button></el-form-item>
      <el-form-item><el-button @click="openCat">新建分类</el-button></el-form-item>
      <el-form-item><el-button type="success" @click="openDoc">上传文档</el-button></el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column prop="categoryName" label="分类" width="120" />
      <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
      <el-table-column prop="versionNo" label="版本" width="70" />
      <el-table-column label="文件" width="90">
        <template #default="{ row }">
          <el-link v-if="row.fileUrl" :href="row.fileUrl" target="_blank" type="primary">打开</el-link>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="80">
        <template #default="{ row }">
          <el-button link type="danger" @click="doDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination v-model:page="query.pageNo" v-model:limit="query.pageSize" :total="total" @pagination="getList" />

    <Dialog v-model="catVisible" title="新建分类" width="400px">
      <el-form ref="catRef" :model="catForm" :rules="catRules" label-width="80px">
        <el-form-item label="名称" prop="name"><el-input v-model="catForm.name" /></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="catVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitCat">保存</el-button>
      </template>
    </Dialog>

    <Dialog v-model="docVisible" title="上传文档" width="520px">
      <el-form ref="docRef" :model="docForm" :rules="docRules" label-width="90px">
        <el-form-item label="分类" prop="categoryId">
          <el-select v-model="docForm.categoryId" filterable class="!w-100%">
            <el-option v-for="c in flatCats" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="标题" prop="title"><el-input v-model="docForm.title" /></el-form-item>
        <el-form-item label="文件URL" prop="fileUrl"><el-input v-model="docForm.fileUrl" placeholder="https://..." /></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="docVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitDoc">保存</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>
<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { FirmOfficeApi, type PracticeDocCategoryVO, type PracticeDocVO } from '@/api/firm/office'
defineOptions({ name: 'FirmPracticeDoc' })
const message = useMessage()
const loading = ref(false)
const list = ref<PracticeDocVO[]>([])
const total = ref(0)
const query = reactive({ pageNo: 1, pageSize: 10 })
const cats = ref<PracticeDocCategoryVO[]>([])
const flatCats = computed(() => {
  const out: PracticeDocCategoryVO[] = []
  const walk = (nodes: PracticeDocCategoryVO[]) => {
    for (const n of nodes || []) {
      out.push(n)
      if (n.children?.length) walk(n.children)
    }
  }
  walk(cats.value)
  return out
})
const saving = ref(false)
const catVisible = ref(false)
const catRef = ref<FormInstance>()
const catForm = reactive({ name: '' })
const catRules: FormRules = { name: [{ required: true, message: '必填', trigger: 'blur' }] }
const docVisible = ref(false)
const docRef = ref<FormInstance>()
const docForm = reactive({ categoryId: undefined as number | undefined, title: '', fileUrl: '' })
const docRules: FormRules = {
  categoryId: [{ required: true, message: '必选', trigger: 'change' }],
  title: [{ required: true, message: '必填', trigger: 'blur' }],
  fileUrl: [{ required: true, message: '必填', trigger: 'blur' }]
}
const loadCats = async () => {
  cats.value = (await FirmOfficeApi.docCategoryTree()) || []
}
const getList = async () => {
  loading.value = true
  try {
    const data = await FirmOfficeApi.docPage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}
const openCat = () => { catForm.name = ''; catVisible.value = true }
const submitCat = async () => {
  await catRef.value?.validate()
  saving.value = true
  try {
    await FirmOfficeApi.docCategoryCreate({ name: catForm.name.trim(), parentId: 0 })
    message.success('已创建分类')
    catVisible.value = false
    await loadCats()
  } finally {
    saving.value = false
  }
}
const openDoc = async () => {
  Object.assign(docForm, { categoryId: undefined, title: '', fileUrl: '' })
  docVisible.value = true
  await loadCats()
}
const submitDoc = async () => {
  await docRef.value?.validate()
  if (!docForm.categoryId) return
  saving.value = true
  try {
    await FirmOfficeApi.docCreate({
      categoryId: docForm.categoryId,
      title: docForm.title.trim(),
      scopeType: 'FIRM',
      fileUrl: docForm.fileUrl.trim()
    })
    message.success('已上传')
    docVisible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}
const doDelete = async (row: PracticeDocVO) => {
  if (!row.id) return
  try { await message.confirm('确认删除？') } catch { return }
  await FirmOfficeApi.docDelete(row.id)
  message.success('已删除')
  await getList()
}
onMounted(async () => {
  await loadCats()
  await getList()
})
</script>
